/**
 * "Сильные стороны и рекомендации" card
 *  - Strengths list (✓)
 *  - Improvements (→) with priority, category, actionItems
 */

import { recommendationParts } from '@aica/core';
import { useTranslation } from 'react-i18next';
import { AnalysisSection, Tag, type XMLAnalysisResult } from './analysis-utils';

interface RecommendationsSectionProps {
  result: XMLAnalysisResult;
}

export function RecommendationsSection({ result }: RecommendationsSectionProps) {
  const { t } = useTranslation('records');

  const strengths = result.recommendations?.strengths ?? [];
  const improvements = result.recommendations?.improvements ?? [];
  const hasStrengths = strengths.length > 0;
  const hasImprovements = improvements.length > 0;

  if (!hasStrengths && !hasImprovements) return null;

  const total = strengths.length + improvements.length;

  return (
    <AnalysisSection
      title={t(
        'records.analysisPanel.strengthsRecommendations',
        'Strengths & Recommendations'
      )}
      summary={<span className="font-mono text-2xs text-fg-subtle">{total}</span>}
      defaultOpen
    >
      {/* Strengths */}
      {hasStrengths && (
        <div className="flex flex-col gap-2">
          {strengths.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs leading-snug">
              <span className="shrink-0 text-success">✓</span>
              <span className="flex-1 text-fg-secondary">{s}</span>
            </div>
          ))}
        </div>
      )}

      {/* Separator */}
      {hasStrengths && hasImprovements && <div className="my-3 h-px bg-border" />}

      {/* Improvements */}
      {hasImprovements && (
        <div className="flex flex-col gap-2.5">
          {improvements.map((imp, index) => {
            // Already joined by normalizeImprovement; demo data and other
            // sources are not, and the call is idempotent.
            const parts = recommendationParts(imp.criterionKey, imp.advice);
            return (
              // The lead is free text and repeats — two items with no lead are
              // both «» — so it is no key on its own.
              <div
                key={`${index}-${imp.criterionKey}`}
                className="flex items-start gap-2"
              >
                <span className="shrink-0 text-accent">→</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-1.5 text-xs leading-snug">
                    <span className="flex-1 text-fg">
                      {/* No lead, no bold heading: the advice stands alone (#1423). */}
                      {parts.lead && (
                        <>
                          <strong className="font-semibold">{parts.lead}</strong>{' '}
                        </>
                      )}
                      {parts.advice && (
                        <span className="text-fg-secondary">{parts.advice}</span>
                      )}
                    </span>
                    {imp.priority && (
                      <Tag
                        variant={
                          imp.priority === 'high'
                            ? 'danger'
                            : imp.priority === 'medium'
                              ? 'warning'
                              : 'neutral'
                        }
                        className="shrink-0 text-micro py-0"
                      >
                        {imp.priority}
                      </Tag>
                    )}
                  </div>
                  {imp.category && (
                    <span className="text-micro text-fg-faint">{imp.category}</span>
                  )}
                  {imp.actionItems && imp.actionItems.length > 0 && (
                    <div className="mt-1 flex flex-col gap-0.5 pl-2">
                      {imp.actionItems.map((ai, aiIdx) => (
                        <div key={aiIdx} className="text-micro text-fg-faint">
                          <span className="text-primary">•</span> {ai.step}
                          {ai.immediateAction && (
                            <span className="ml-1 text-warning">
                              → {ai.immediateAction}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AnalysisSection>
  );
}
